import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import {
  Comment as CommentIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import {format} from 'date-fns';
import {pl} from 'date-fns/locale';
import {useSnackbar} from 'notistack';
import {
  useAcquisitionTypesServiceGetAcquisitionTypes,
  useLeadCrudServiceGetApiLeadContactsPaginated,
  useOffersServiceGetApiOffers,
  useUserServiceGetApiUserMe
} from '../api/generated/queries/queries';
import {LeadDTO} from '../api/generated/requests/types.gen';
import {MinIoFileStorageService} from '../api/generated/requests/services.gen';
import LeadContactDialog from '../components/Leads/LeadContactDialog';
import LeadDetailDialog from '../components/Leads/LeadDetailDialog';
import DeleteConfirmDialog from '../components/Common/DeleteConfirmDialog';
import {useContactMutations} from '../hooks/useContactData';
import LeadSearchBar from '../components/Leads/LeadSearchBar';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import ColumnConfigDialog, {ColumnConfig} from '../components/Common/ColumnConfigDialog';

type LeadColumn = {
  id: keyof LeadDTO | 'actions';
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
};

const defaultColumns: LeadColumn[] = [
  { id: 'id', label: 'Id' },
  { id: 'firstName', label: 'Imię' },
  { id: 'lastName', label: 'Nazwisko' },
  { id: 'name', label: 'Nazwa firmy' },
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Telefon' },
  { id: 'city', label: 'Miasto' },
  { id: 'nip', label: 'NIP' },
  { id: 'offer', label: 'Oferta' },
  { id: 'acquisitionType', label: 'Źródło' },
  { id: 'createdAt', label: 'Data utworzenia' },
  { id: 'actions', label: 'Akcje' }
];

const LeadsPage: React.FC = () => {
  const { createContact, updateContact, deleteContact } = useContactMutations();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: acquisitionTypes } = useAcquisitionTypesServiceGetAcquisitionTypes();
  const { data: offers } = useOffersServiceGetApiOffers();
  const { data: currentUser } = useUserServiceGetApiUserMe();
  const { enqueueSnackbar } = useSnackbar();
  
  // Check if user is admin
  const isAdmin = currentUser?.roles?.includes('api-admin');
  const [columns, setColumns] = useState<LeadColumn[]>(defaultColumns);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Filter state variables
  const [searchTerm, setSearchTerm] = useState('');
  const [acquisitionTypeFilter, setAcquisitionTypeFilter] = useState('');
  const [offerFilter, setOfferFilter] = useState<number | ''>('');
  const [createdAtFromFilter, setCreatedAtFromFilter] = useState<Date | null>(null);
  const [createdAtToFilter, setCreatedAtToFilter] = useState<Date | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Server-side pagination state
  const [sortBy, setSortBy] = useState<'ID' | 'CREATED_AT' | 'EMAIL' | 'PHONE' | 'FIRST_NAME' | 'LAST_NAME' | 'NAME' | 'CITY' | 'NIP' | 'OFFER' | 'ACQUISITION_TYPE'>('ID');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');

  // Fetch paginated lead data
  const { data: leadPagedResponse, isLoading, refetch: refreshContacts } = useLeadCrudServiceGetApiLeadContactsPaginated({
    page,
    size: rowsPerPage,
    searchTerm: searchTerm || undefined,
    acquisitionTypeId: acquisitionTypeFilter ? Number(acquisitionTypeFilter) : undefined,
    offer: offerFilter || undefined,
    createdFrom: createdAtFromFilter ? createdAtFromFilter.toISOString() : undefined,
    createdTo: createdAtToFilter ? createdAtToFilter.toISOString() : undefined,
    sortBy,
    sortDirection
  });

  // Extract data from paginated response
  const leadContacts = leadPagedResponse?.content || [];
  const pageInfo = leadPagedResponse?.page;
  const totalElements = pageInfo?.totalElements || 0;

// Automatyczne otwarcie dialogu edycji, jeśli w URL jest ?edit=ID
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');
    const detailId = params.get('detail');

    if (editId && leadContacts) {
      const leadToEdit = leadContacts.find((lead) => lead.id === Number(editId));
      if (leadToEdit) {
        handleOpenDialog(leadToEdit);
      }
    } else if (detailId && leadContacts) {
      const leadToDetail = leadContacts.find((lead) => lead.id === Number(detailId));
      if (leadToDetail) {
        handleOpenDetailDialog(leadToDetail, 0);
      }
    }
  }, [location.search, leadContacts]);

  // Handle URL parameter for immediate dialog opening (like TasksPage)
  useEffect(() => {
    const leadId = searchParams.get('detail');
    if (leadId) {
      const parsedId = parseInt(leadId, 10);
      if (!isNaN(parsedId)) {
        setSelectedLeadId(parsedId);
        setOpenDetailDialog(true);
      }
    }
  }, [searchParams]);

  // Stan dla dialogów
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState<LeadDTO | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadFiles, setLeadFiles] = useState<any[]>([]);

  // Funkcja do obsługi kliknięcia nagłówka kolumny
  const handleSort = (key: keyof LeadDTO) => {
    // Map LeadDTO fields to backend sort fields
    const sortFieldMap: Record<keyof LeadDTO, 'ID' | 'CREATED_AT' | 'EMAIL' | 'PHONE' | 'FIRST_NAME' | 'LAST_NAME' | 'NAME' | 'CITY' | 'NIP' | 'OFFER' | 'ACQUISITION_TYPE'> = {
      id: 'ID',
      firstName: 'FIRST_NAME',
      lastName: 'LAST_NAME',
      name: 'NAME',
      email: 'EMAIL',
      phone: 'PHONE',
      city: 'CITY',
      nip: 'NIP',
      offer: 'OFFER',
      acquisitionType: 'ACQUISITION_TYPE',
      createdAt: 'CREATED_AT',
      // Default mappings for other fields
      updatedAt: 'CREATED_AT',
      deletedAt: 'CREATED_AT',
      prefixId: 'ID',
      postalCode: 'CITY',
      street: 'CITY',
      houseNumber: 'CITY',
      apartmentNumber: 'CITY',
      alternatePhone: 'PHONE'
    };

    const backendSortField = sortFieldMap[key];
    if (!backendSortField) return;

    // Toggle direction if same field, otherwise start with ASC
    const newDirection: 'ASC' | 'DESC' = 
      sortBy === backendSortField && sortDirection === 'ASC' ? 'DESC' : 'ASC';

    setSortBy(backendSortField);
    setSortDirection(newDirection);
    setPage(0); // Reset page when sorting changes
  };

  // Paginacja
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Obsługa formularza
  const handleOpenDialog = (lead: LeadDTO | null = null) => {
    setSelectedLead(lead);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLead(null);
    navigate('/leads'); // czyści query param
  };

  const handleOpenDeleteDialog = async (lead: LeadDTO) => {
    setSelectedLead(lead);

    // Fetch associated files
    if (lead.id) {
      try {
        const files = await MinIoFileStorageService.getApiFilesByRelatedObjectByIdByType({
          id: lead.id.toString(),
          type: 'LEAD'
        });
        setLeadFiles(files || []);
      } catch (error) {
        console.error('Error fetching lead files:', error);
        setLeadFiles([]);
      }
    } else {
      setLeadFiles([]);
    }

    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedLead(null);
    setLeadFiles([]);
  };

  // Obsługa dialogu szczegółów
  const handleOpenDetailDialog = (lead: LeadDTO, initialTab = 0) => {
    if (lead.id) {
      setSelectedLeadId(lead.id);
      setSelectedTabIndex(initialTab);
      setOpenDetailDialog(true);
    }
  };

  // Obsługa otwierania dialogu z zakładką komentarzy
  const handleOpenCommentsDialog = (e: React.MouseEvent, lead: LeadDTO) => {
    e.stopPropagation(); // Prevent row click event
    handleOpenDetailDialog(lead, 1); // 1 is the index of the comments tab
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setSelectedLeadId(null);
    setSelectedTabIndex(0); // Reset tab index to default (contact details)
  };

  // Zapisywanie i aktualizacja leada
  const handleSaveLead = async (lead: LeadDTO, createNext?: boolean): Promise<LeadDTO | void> => {
    setIsSubmitting(true);
    try {
      let result: LeadDTO;
      if (lead.id) {
        // Aktualizacja istniejącego leada
        result = await updateContact({
          id: lead.id,
          requestBody: lead
        });
      } else {
        // Utworzenie nowego leada
        result = await createContact({
          requestBody: lead
        });
      }

      if (!createNext) {
        handleCloseDialog();
      }

      return result;
    } catch (error) {
      console.error('Błąd podczas zapisywania leada:', error);
      enqueueSnackbar('Wystąpił błąd podczas zapisywania leada', { variant: 'error' });
      throw error; // Re-throw to let LeadContactDialog handle it
    } finally {
      setIsSubmitting(false);
    }
  };

  // Usuwanie leada
  const handleDeleteLead = () => {
    if (!selectedLead?.id) return;

    setIsSubmitting(true);
    try {
      deleteContact({
        id: selectedLead.id
      });
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Błąd podczas usuwania leada:', error);
      enqueueSnackbar('Wystąpił błąd podczas usuwania leada', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Inicjalizacja kolumn z localStorage
  useEffect(() => {
    const savedConfig = localStorage.getItem('leads-columns-config');
    if (savedConfig) {
      try {
        const parsedConfig: ColumnConfig[] = JSON.parse(savedConfig);
        if (Array.isArray(parsedConfig)) {
          // Convert ColumnConfig back to LeadColumn format and filter visible columns
          const visibleColumns = parsedConfig
            .filter(config => config.visible)
            .map(config => {
              const originalColumn = defaultColumns.find(col => col.id === config.id);
              return originalColumn ? {
                ...originalColumn,
                label: config.label
              } : null;
            })
            .filter(Boolean) as LeadColumn[];
          
          if (visibleColumns.length > 0) {
            setColumns(visibleColumns);
          }
        }
      } catch (error) {
        console.error('Błąd podczas parsowania zapisanych kolumn:', error);
      }
    }
  }, []);

  // Obsługa konfiguracji kolumn
  const handleColumnConfigSave = (columnConfigs: ColumnConfig[]) => {
    // Save configuration to localStorage
    localStorage.setItem('leads-columns-config', JSON.stringify(columnConfigs));
    
    // Convert ColumnConfig to LeadColumn format and filter visible columns
    const visibleColumns = columnConfigs
      .filter(config => config.visible)
      .map(config => {
        const originalColumn = defaultColumns.find(col => col.id === config.id);
        return originalColumn ? {
          ...originalColumn,
          label: config.label
        } : null;
      })
      .filter(Boolean) as LeadColumn[];
    
    setColumns(visibleColumns);
    enqueueSnackbar('Konfiguracja kolumn została zapisana', { variant: 'success' });
  };

  const getColumnConfigs = (): ColumnConfig[] => {
    const savedConfig = localStorage.getItem('leads-columns-config');
    if (savedConfig) {
      try {
        const parsedConfig: ColumnConfig[] = JSON.parse(savedConfig);
        if (Array.isArray(parsedConfig)) {
          return parsedConfig;
        }
      } catch (error) {
        console.error('Błąd podczas parsowania zapisanych kolumn:', error);
      }
    }
    
    // Return default configuration
    return defaultColumns.map(col => ({
      id: col.id.toString(),
      label: col.label,
      visible: true,
      minWidth: col.minWidth,
      align: col.align
    }));
  };


  return (
      <>
        <LeadSearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          acquisitionTypeFilter={acquisitionTypeFilter}
          setAcquisitionTypeFilter={setAcquisitionTypeFilter}
          offerFilter={offerFilter}
          setOfferFilter={setOfferFilter}
          createdAtFromFilter={createdAtFromFilter}
          setCreatedAtFromFilter={setCreatedAtFromFilter}
          createdAtToFilter={createdAtToFilter}
          setCreatedAtToFilter={setCreatedAtToFilter}
          setPage={setPage}
          refreshLeads={async () => { await refreshContacts(); }}
          acquisitionTypes={acquisitionTypes}
          offers={offers}
        />

        <Paper sx={{
          width: '100%',
          overflow: 'hidden',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          bgcolor: '#ffffff',
        }}>
          {isLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
          ) : (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  backgroundColor: '#f9fafb',
                  borderBottom: '1px solid #e5e7eb',
                  px: 2,
                  py: 0
                }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SettingsIcon />}
                    onClick={() => setColumnConfigOpen(true)}
                    sx={{
                      fontSize: '0.8rem',
                      minWidth: 'auto',
                      px: 2,
                      borderRadius: '9999px'
                    }}
                  >
                    Kolumny
                  </Button>
                  <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={totalElements}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      labelRowsPerPage="Wierszy na stronę:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} z ${count}`}
                      sx={{
                        '.MuiTablePagination-toolbar': {
                          backgroundColor: 'transparent',
                          fontSize: '0.8rem',
                          border: 'none',
                        },
                        '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                          fontSize: '0.8rem',
                        },
                      }}
                  />
                </Box>
                <TableContainer>
                  <Table
                      stickyHeader
                      sx={{
                        '& td, & th': {
                          fontSize: '0.8rem',
                          paddingY: '4px',
                          paddingX: '8px',
                        },
                      }}
                  >
                    <TableHead>
                      <TableRow sx={{ height: 48 }}>
                        {columns.map((column) => (
                          <TableCell
                            key={column.id.toString()}
                            onClick={() => column.id !== 'actions' && handleSort(column.id as keyof LeadDTO)}
                            align={column.align}
                            sx={{
                              minWidth: column.minWidth,
                              backgroundColor: '#ffffff',
                              color: '#1f2937',
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              borderBottom: '1px solid #e5e7eb',
                              cursor: column.id !== 'actions' ? 'pointer' : 'default',
                              userSelect: 'none'
                            }}
                          >
                            {column.label}
                            {column.id !== 'actions' && (() => {
                              const sortFieldMap: Record<string, string> = {
                                'id': 'ID',
                                'firstName': 'FIRST_NAME',
                                'lastName': 'LAST_NAME',
                                'name': 'NAME',
                                'email': 'EMAIL',
                                'phone': 'PHONE',
                                'city': 'CITY',
                                'nip': 'NIP',
                                'offer': 'OFFER',
                                'acquisitionType': 'ACQUISITION_TYPE',
                                'createdAt': 'CREATED_AT'
                              };
                              const currentSortField = sortFieldMap[column.id as string];
                              return currentSortField === sortBy && (
                                <Box component="span" ml={1}>
                                  {sortDirection === 'ASC' ? '↑' : '↓'}
                                </Box>
                              );
                            })()}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leadContacts.map((lead, index) => (
                              <TableRow
                                  key={lead.id}
                                  hover
                                  onClick={() => handleOpenDetailDialog(lead)}
                                  sx={{
                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                      backgroundColor: '#e0f2fe',
                                    },
                                    height: '36px',
                                    '& td': {
                                      py: '4px',
                                    },
                                  }}
                              >
                                {columns.map((column) => {
                                  // Renderowanie komórki w zależności od typu kolumny
                                  if (column.id === 'id') {
                                    return (
                                      <TableCell key={column.id}>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.8rem' }}>
                                          {lead.prefixId && lead.id ? `${lead.prefixId}-${lead.id}` : lead.id}
                                        </Typography>
                                      </TableCell>
                                    );
                                  }
                                  if (column.id === 'firstName') {
                                    return <TableCell key={column.id}>{lead.firstName}</TableCell>;
                                  }
                                  if (column.id === 'lastName') {
                                    return <TableCell key={column.id}>{lead.lastName}</TableCell>;
                                  }
                                  if (column.id === 'name') {
                                    return <TableCell key={column.id}>{lead.name || '-'}</TableCell>;
                                  }
                                  if (column.id === 'email') {
                                    return <TableCell key={column.id}>{lead.email}</TableCell>;
                                  }
                                  if (column.id === 'phone') {
                                    return <TableCell key={column.id}>{lead.phone || '-'}</TableCell>;
                                  }
                                  if (column.id === 'city') {
                                    return <TableCell key={column.id}>{lead.city || '-'}</TableCell>;
                                  }
                                  if (column.id === 'nip') {
                                    return <TableCell key={column.id}>{lead.nip || '-'}</TableCell>;
                                  }
                                  if (column.id === 'offer') {
                                    const offer = offers?.find(o => o.id === lead.offer);
                                    return <TableCell key={column.id}>{offer?.name || '-'}</TableCell>;
                                  }
                                  if (column.id === 'acquisitionType') {
                                    return <TableCell key={column.id}>{lead.acquisitionType?.name || '-'}</TableCell>;
                                  }
                                  if (column.id === 'createdAt') {
                                    return <TableCell key={column.id}>
                                      {lead.createdAt
                                          ? format(new Date(lead.createdAt), 'dd MMM yyyy, HH:mm', { locale: pl })
                                          : '-'}
                                    </TableCell>;
                                  }
                                  if (column.id === 'actions') {
                                    return <TableCell key={column.id} align="right">
                                      <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={(e) => {
                                            e.stopPropagation(); // Prevent row click event
                                            handleOpenDetailDialog(lead, 0);
                                          }}
                                          title="Edytuj"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                          size="small"
                                          color="info"
                                          onClick={(e) => handleOpenCommentsDialog(e, lead)}
                                          title="Komentarze"
                                      >
                                        <CommentIcon fontSize="small" />
                                      </IconButton>
                                      {isAdmin && (
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={(e) => {
                                              e.stopPropagation(); // Prevent row click event
                                              handleOpenDeleteDialog(lead);
                                            }}
                                            title="Usuń"
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </TableCell>;
                                  }
                                  return null;
                                })}
                              </TableRow>
                          ))}
                      {leadContacts.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              Nie znaleziono kontaków
                            </TableCell>
                          </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalElements}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Wierszy na stronę:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} z ${count}`}
                    sx={{
                      '.MuiTablePagination-toolbar': {
                        backgroundColor: '#f9fafb',
                        fontSize: '0.8rem',
                        borderTop: '1px solid #e5e7eb',
                      },
                      '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                        fontSize: '0.8rem',
                      },
                    }}
                />
              </>
          )}
        </Paper>

        {/* Dialog do dodawania/edycji leada */}
        <LeadContactDialog
            open={openDialog}
            onClose={handleCloseDialog}
            lead={selectedLead}
            onSave={handleSaveLead}
            isSubmitting={isSubmitting}
        />

        {/* Dialog do potwierdzenia usunięcia */}
        <DeleteConfirmDialog
            open={openDeleteDialog}
            onClose={handleCloseDeleteDialog}
            onConfirm={handleDeleteLead}
            title="Potwierdź usunięcie"
            content={`Czy na pewno chcesz usunąć lead: ${selectedLead?.firstName} ${selectedLead?.lastName}?${leadFiles.length > 0 ? `\n\nUwaga: Ten lead ma ${leadFiles.length} powiązanych plików, które po usunięciu leada będą przeniesione do kosza.` : ''}`}
            isSubmitting={isSubmitting}
        />

        {/* Dialog szczegółowy leada */}
        <LeadDetailDialog
            open={openDetailDialog}
            leadId={selectedLeadId}
            onClose={handleCloseDetailDialog}
            onLeadUpdated={async () => {
              await refreshContacts();
            }}
            initialTab={selectedTabIndex}
        />

        {/* Dialog konfiguracji kolumn */}
        <ColumnConfigDialog
          open={columnConfigOpen}
          onClose={() => setColumnConfigOpen(false)}
          columns={getColumnConfigs()}
          onSave={handleColumnConfigSave}
          title="Konfiguracja kolumn kontaków"
        />

      </>
  );
};

export default LeadsPage;
